import * as Sentry from "@sentry/react";
import { preconnect } from "react-dom";
import { createRoot } from "react-dom/client";
import { RouterProvider } from "@tanstack/react-router";
import { IMAGE_HOST } from "./lib/marketing-assets";
import { setDeliveryHost } from "@shared/image-url";
import { captureFirstTouch } from "./lib/first-touch";
import { isReloadPending, markReloadPending } from "./lib/stale-bundle";
import { installTranslateDomGuard } from "./lib/translate-dom-guard";
import { router } from "./router";
import "./index.css";

// Point the shared image-URL builder at THIS environment's zone. `shared/` can't read
// `import.meta.env`, so the client hands it in here — before anything builds/renders an
// image URL. Unset (prod) → the shared prod fallback. Without this, a dev/staging client
// stamps the prod host into uploaded-image URLs, and its own bytes 404 (dev bucket only).
setDeliveryHost(import.meta.env.VITE_CLOUDFLARE_IMAGE_HOST);
// First-visit attribution cookie (BDAY-195) — before routing can rewrite the URL's query.
captureFirstTouch();

// Warm the image-CDN connection (DNS + TLS) before the first card/marketing
// image request — card art loads from a separate origin on nearly every page.
preconnect(IMAGE_HOST);

// Stale-bundle recovery (Sentry BDAY-WEB-4, 2026-10-15). A tab that was open across a
// deploy still holds the old index.html; its first lazy route then asks for a hashed chunk
// or CSS file the new build no longer ships, Vite's preload helper throws
// "Unable to preload CSS for …" / "Failed to fetch dynamically imported module", and the
// user sees a broken page. Vite fires `vite:preloadError` for exactly this; reloading picks
// up the new index.html. Guarded once per build id so a genuinely missing asset can't loop.
window.addEventListener("vite:preloadError", (event) => {
  const key = `bday:preload-reload:${import.meta.env.VITE_BUILD_ID ?? "dev"}`;
  try {
    if (sessionStorage.getItem(key)) return; // already reloaded once for this build — let it surface
    sessionStorage.setItem(key, String(Date.now()));
  } catch {
    // storage unavailable (private mode) — still worth one reload; the default throw is worse
  }
  event.preventDefault();
  markReloadPending();
  window.location.reload();
});

const sentryDsn = import.meta.env.VITE_SENTRY_DSN_WEB;
if (sentryDsn) {
  Sentry.init({
    dsn: sentryDsn,
    release: import.meta.env.VITE_BUILD_ID,
    enabled: import.meta.env.MODE !== "development",
    // VITE_ENVIRONMENT is the deploy env (staging/production); MODE is "production" for
    // every Vite production build, which would tag staging events as production.
    environment: import.meta.env.VITE_ENVIRONMENT || import.meta.env.MODE,
    // Replay is added lazily below (lib/sentry-replay.ts) — it is ~38 KiB that only matters
    // once an error happens; keeping it out of the main bundle shortens every first paint.
    integrations: [Sentry.browserTracingIntegration()],
    // Launch posture (PRD 13 D4, 2026-09-25): errors are the signal. Traces at 2% and NO
    // ambient session replays — 10% of sessions would exhaust the replay quota in an hour on
    // cutover morning; a replay is recorded only when an error happens.
    // 0.25 (CWV pass, 2026-11-03): "on error" still means every sampled session BUFFERS —
    // the rrweb DOM snapshot is a ~400 ms main-thread task a few seconds into every page
    // load (24-tile grids), the likeliest source of the ~210 ms INP Search Console reports.
    // Sentry draws the sample at session start, so 75% of visitors never pay it; a quarter
    // of errors still come with a replay.
    tracesSampleRate: import.meta.env.PROD ? 0.02 : 1.0,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0.25,
    beforeSend(event) {
      // A stale-bundle reload is already under way — whatever breaks before the page
      // unloads is fallout from the old bundle, not a bug (BDAY-WEB-5/-6/-9).
      if (isReloadPending()) return null;

      const msg = event.exception?.values?.[0]?.value ?? "";

      // Microsoft's link scanners (Outlook Safe Links / Defender) open links from emails in
      // a headless browser whose injected script rejects with this exact string — not our
      // code, and each one also spent a session replay (BDAY-WEB-A, 24 events on 10-21).
      if (msg.includes("Object Not Found Matching Id:")) {
        return null;
      }

      // A SYNTHETIC unhandledrejection (isTrusted: false) is an event some script built and
      // dispatched itself — an extension or injected code, never a real promise of ours
      // (BDAY-WEB-P: a bare CustomEvent on /notifications, no stack). Real rejections are
      // browser-dispatched and trusted, so they still report.
      const serialized = (event.extra as { __serialized__?: { isTrusted?: unknown } } | undefined)
        ?.__serialized__;
      if (
        msg.includes("(type=unhandledrejection) captured as promise rejection") &&
        serialized?.isTrusted === false
      ) {
        return null;
      }

      // Drop errors originating from browser extensions (e.g. Safari Web Extensions
      // on iOS calling runtime.sendMessage() into a closed/missing tab context).
      if (msg.includes("runtime.sendMessage") || msg.includes("Tab not found")) {
        return null;
      }

      // Drop transient chunk/module load failures on mobile WebKit — not actionable.
      if (
        msg.includes("Failed to fetch dynamically imported module") ||
        msg.includes("Loading chunk") ||
        msg.includes("Loading CSS chunk") ||
        msg.includes("Unable to preload CSS") ||
        msg.includes("error loading dynamically imported module") ||
        msg.includes("Importing a module script failed") || // Safari's wording (BDAY-WEB-D)
        msg.includes("Load failed")
      ) {
        return null;
      }

      // Drop minified single-token errors (e.g. "Sd", "Xr") with no useful stack —
      // typically transient WebKit module evaluation failures on iOS.
      if (/^[A-Z][a-z]$/.test(msg)) {
        return null;
      }

      // Drop errors thrown by the Facebook in-app browser's injected scripts
      // (contact autofill bridge, etc.) — not our code, not actionable.
      const frames = event.exception?.values?.[0]?.stacktrace?.frames ?? [];
      if (
        frames.some(
          (f) =>
            f.filename?.startsWith("iabjs://") ||
            f.function?.includes("setContactAutofillValuesFromBridge"),
        )
      ) {
        return null;
      }

      // From here on, only errors with NONE of our code on the stack are dropped: our own
      // code always has one of our /assets/ chunks there, so a matching message from our code
      // still reports.
      const ours = frames.some((f) => (f.filename ?? "").includes("/assets/"));

      // Browser extensions' own files on the stack (PayPal Honey's h0.js — BDAY-WEB-15).
      if (
        !ours &&
        frames.some((f) =>
          /^(chrome|moz|safari(-web)?)-extension:|\.safariextension\/|\.appex\//.test(
            f.filename ?? "",
          ),
        )
      ) {
        return null;
      }

      // DuckDuckGo's injected privacy scripts talk to the browser over WebKit message handlers
      // and reject when the handler is gone (BDAY-WEB-T, -10, -Z: DuckDuckGo on Mac and iPhone,
      // no stack). Our app's web views use window.ReactNativeWebView, never window.webkit.
      if (
        !ours &&
        (msg.includes("WKWebView API client did not respond") ||
          msg.includes("Script message handler is unavailable") ||
          msg.includes("window.webkit.messageHandlers"))
      ) {
        return null;
      }

      // An Android app's in-app browser calling its own JS bridge after the app dropped it
      // ("Error invoking needInjectCss / log: Java object is gone" — BDAY-WEB-1G, -1D). We
      // add no Java bridge; our app's web views use window.ReactNativeWebView.
      if (!ours && msg.includes("Java object is gone")) return null;

      // A promise rejected with an EMPTY OBJECT, no stack: desktop Safari only, on any page,
      // even with the member idle (BDAY-WEB-12, 10 events, 5 users). Our code never rejects with
      // a bare object (checked 2026-11-03); Safari extensions such as PayPal Honey do, and one
      // event matched a Honey error (BDAY-WEB-15) by browser, page and time.
      if (
        !ours &&
        frames.length === 0 &&
        msg.includes("captured as promise rejection with keys: [object has no keys]")
      ) {
        return null;
      }

      // Injected code that tries to eval() under our CSP (BDAY-WEB-8, <anonymous> frames), and
      // in-app browsers on Android whose injected script doesn't parse — WebView labels it with
      // the page url, e.g. "/reminders" (BDAY-WEB-14, -17: "Unexpected token 'else'").
      const type = event.exception?.values?.[0]?.type ?? "";
      if (!ours && (type === "EvalError" || type === "SyntaxError")) {
        return null;
      }

      // Chrome for iOS and the Google app inject their own scripts into every page, and one
      // of them recurses until it throws. WebKit labels injected scripts with the PAGE url,
      // so every frame reads "/reminders:229" and none is one of our /assets/ chunks
      // (BDAY-444 / Sentry BDAY-WEB-V). Our own code always has a chunk on the stack.
      if (
        /\b(GSA|CriOS)\//.test(navigator.userAgent) &&
        frames.length > 0 &&
        frames.every((f) => !(f.filename ?? "").includes("/assets/"))
      ) {
        return null;
      }

      return event;
    },
  });

  // Session Replay (error-only) joins once the page is idle, as a separate chunk — never
  // for crawlers or automated browsers: Googlebot renders the page with JS, and a replay of
  // a bot's session is pure main-thread cost with nothing to watch (2026-11-03).
  if (!isAutomatedVisitor()) {
    const addReplay = () =>
      import("./lib/sentry-replay")
        .then((m) => Sentry.addIntegration(m.createReplay()))
        .catch(() => {});
    if ("requestIdleCallback" in window) {
      window.requestIdleCallback(() => void addReplay(), { timeout: 4000 });
    } else {
      setTimeout(() => void addReplay(), 2000);
    }
  }
}

/**
 * Search/social crawlers and automation-driven browsers (Lighthouse, PageSpeed Insights,
 * Playwright, the GSC URL inspection renderer). The UA list mirrors the search + social
 * classes in server/lib/og.ts; `navigator.webdriver` covers headless automation that keeps
 * a plain Chrome UA.
 */
function isAutomatedVisitor(): boolean {
  try {
    if (navigator.webdriver) return true;
    return /bot|crawl|spider|slurp|lighthouse|chrome-lighthouse|headless|google-inspectiontool|googleother|storebot|facebookexternalhit|twitterbot|linkedinbot|pinterest|whatsapp|telegrambot|discordbot|slackbot|skypeuripreview|applebot|duckduckbot|yandex|baiduspider|bingpreview|petalbot|semrush|ahrefs|mj12bot/i.test(
      navigator.userAgent,
    );
  } catch {
    return false;
  }
}

// Before the first render: page translation must not be able to crash the React tree
// (BDAY-WEB-B / -C — see lib/translate-dom-guard.ts).
installTranslateDomGuard();

createRoot(document.getElementById("root")!).render(<RouterProvider router={router} />);
